/**
 * Team page (STAFF_ACCESS_PLAN Phase 3) — owner-only staff management.
 * List staff, add with a capability picker (Manager/Staff presets), edit
 * access, deactivate/reactivate, reset password. The backend enforces
 * owner-only access; staff never see this page (nav hides it too).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UserCog, Plus, KeyRound, Power, Pencil, X } from 'lucide-react';
import { vendorApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface StaffMember {
  id: number;
  user_id: number;
  name: string;
  email: string;
  phone: string | null;
  role_label: string | null;
  capabilities: string[];
  is_active: boolean;
  last_active_at: string | null;
  invited_by_name: string | null;
  created_at: string | null;
}

const CAPABILITY_LABELS: Record<string, string> = {
  'schedule.view': 'View bookings & schedule',
  'bookings.create': 'Log manual bookings',
  'bookings.edit': 'Reschedule bookings',
  'bookings.cancel': 'Cancel bookings & refunds',
  'checkin': 'Check in customers (QR scan)',
  'slots.manage': 'Block time & closures',
  'venue.edit': 'Edit venue info & courts',
  'pricing.manage': 'Pricing, offers & promotions',
  'memberships.manage': 'Memberships',
  'reviews.reply': 'Reply to reviews',
  'insights.view': 'Customer insights & analytics',
  'money.view': 'View earnings (read-only)',
  'activity.view': 'View activity log',
};

// The picker groups capabilities the way the dashboard is laid out, so the
// owner reasons in terms of tabs rather than a flat list. Only capabilities
// the backend actually returns (StaffCapabilities::all) are rendered — a
// capability missing from every group falls into "Other" so nothing is lost.
const CAPABILITY_GROUPS: { label: string; capabilities: string[] }[] = [
  {
    label: 'Bookings & front desk',
    capabilities: ['schedule.view', 'bookings.create', 'bookings.edit', 'bookings.cancel', 'checkin', 'slots.manage'],
  },
  {
    label: 'Venue setup',
    capabilities: ['venue.edit', 'pricing.manage', 'memberships.manage'],
  },
  {
    label: 'Customers & reputation',
    capabilities: ['insights.view', 'reviews.reply'],
  },
  {
    label: 'Oversight',
    capabilities: ['activity.view'],
  },
];

// Grantable in the catalog but with no staff-facing UI yet (money tabs are
// owner-only), so we don't surface it — granting it would do nothing visible.
const HIDDEN_CAPABILITIES = ['money.view'];

// Friendlier names for the preset quick-start buttons.
const PRESET_LABELS: Record<string, string> = {
  staff: 'Front desk',
  manager: 'Manager',
};

export default function TeamNew() {
  const { user } = useAuth();
  const venue = user?.venues?.[0];
  const venueId = venue?.id as number | undefined;

  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [flagEnabled, setFlagEnabled] = useState(true);
  const [allCapabilities, setAllCapabilities] = useState<string[]>([]);
  const [presets, setPresets] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Add/edit modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<StaffMember | null>(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', role_label: '' });
  const [selectedCaps, setSelectedCaps] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const [staffData, capData] = await Promise.all([
        vendorApi.getStaff(venueId),
        vendorApi.getStaffCapabilities(venueId),
      ]);
      setStaff(staffData.staff);
      setFlagEnabled(staffData.staff_management_enabled);
      setAllCapabilities(capData.capabilities);
      setPresets(capData.presets);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load team');
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', email: '', phone: '', role_label: '' });
    setSelectedCaps(presets['staff'] ?? []);
    setModalError(null);
    setModalOpen(true);
  };

  const openEdit = (member: StaffMember) => {
    setEditing(member);
    setForm({ name: member.name, email: member.email, phone: member.phone ?? '', role_label: member.role_label ?? '' });
    setSelectedCaps(member.capabilities);
    setModalError(null);
    setModalOpen(true);
  };

  const toggleCap = (cap: string) => {
    setSelectedCaps(prev => prev.includes(cap) ? prev.filter(c => c !== cap) : [...prev, cap]);
  };

  const applyPreset = (name: string) => setSelectedCaps(presets[name] ?? []);

  // Select-all / clear for a whole group.
  const toggleGroup = (groupCaps: string[]) => {
    const allSelected = groupCaps.every(c => selectedCaps.includes(c));
    setSelectedCaps(prev => allSelected
      ? prev.filter(c => !groupCaps.includes(c))
      : Array.from(new Set([...prev, ...groupCaps])));
  };

  // Build the render groups from the backend's grantable list: known groups in
  // order, then any leftover grantable capability under "Other" (defensive —
  // so a newly-added backend capability never silently disappears).
  const available = allCapabilities.filter(c => !HIDDEN_CAPABILITIES.includes(c));
  const grouped = CAPABILITY_GROUPS
    .map(g => ({ label: g.label, capabilities: g.capabilities.filter(c => available.includes(c)) }))
    .filter(g => g.capabilities.length > 0);
  const claimed = new Set(grouped.flatMap(g => g.capabilities));
  const otherCaps = available.filter(c => !claimed.has(c));
  const renderGroups = otherCaps.length > 0
    ? [...grouped, { label: 'Other', capabilities: otherCaps }]
    : grouped;

  const handleSave = async () => {
    if (!venueId) return;
    if (selectedCaps.length === 0) {
      setModalError('Select at least one permission.');
      return;
    }
    setSaving(true);
    setModalError(null);
    try {
      if (editing) {
        await vendorApi.updateStaff(venueId, editing.id, {
          role_label: form.role_label || null,
          capabilities: selectedCaps,
        });
        setToast('Access updated.');
      } else {
        const res = await vendorApi.createStaff(venueId, {
          name: form.name,
          email: form.email,
          phone: form.phone || undefined,
          role_label: form.role_label || undefined,
          capabilities: selectedCaps,
        });
        setToast(res.message || 'Staff member added.');
      }
      setModalOpen(false);
      await load();
    } catch (e: any) {
      setModalError(e?.response?.data?.message || e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (member: StaffMember) => {
    if (!venueId) return;
    try {
      if (member.is_active) {
        if (!window.confirm(`Deactivate ${member.name}? They lose dashboard access immediately; their history is kept.`)) return;
        await vendorApi.deactivateStaff(venueId, member.id);
        setToast(`${member.name} deactivated.`);
      } else {
        await vendorApi.reactivateStaff(venueId, member.id);
        setToast(`${member.name} reactivated.`);
      }
      await load();
    } catch (e: any) {
      setToast(e?.response?.data?.message || e?.message || 'Action failed');
    }
  };

  const handleResetPassword = async (member: StaffMember) => {
    if (!venueId) return;
    if (!window.confirm(`Email ${member.name} a new temporary password? They must change it on next login.`)) return;
    try {
      const res = await vendorApi.resetStaffPassword(venueId, member.id);
      setToast(res.message || 'Temporary password emailed.');
    } catch (e: any) {
      setToast(e?.response?.data?.message || e?.message || 'Reset failed');
    }
  };

  const activeCount = useMemo(() => staff.filter(s => s.is_active).length, [staff]);

  if (loading) {
    return <div className="p-8 text-gray-500">Loading team…</div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white text-sm px-4 py-3 rounded-lg shadow-lg whitespace-pre-line">
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <UserCog className="w-5 h-5" /> Team
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {activeCount} active member{activeCount === 1 ? '' : 's'} · every action a staff member takes is recorded under their own name
          </p>
        </div>
        <button
          onClick={openAdd}
          disabled={!flagEnabled}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg"
        >
          <Plus className="w-4 h-4" /> Add staff
        </button>
      </div>

      {!flagEnabled && (
        <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg p-4">
          Staff management is not enabled for this venue yet. Contact Spotts support to enable it.
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-4">{error}</div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {staff.length === 0 ? (
          <div className="p-10 text-center text-gray-500 text-sm">
            No staff yet. Add your first team member — they get their own login and only the permissions you pick.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {staff.map(member => (
              <li key={member.id} className={`p-4 md:px-6 flex flex-col md:flex-row md:items-center gap-3 ${!member.is_active ? 'opacity-60' : ''}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900">{member.name}</span>
                    {member.role_label && (
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{member.role_label}</span>
                    )}
                    {!member.is_active && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Deactivated</span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 truncate">{member.email}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {member.capabilities.length} permission{member.capabilities.length === 1 ? '' : 's'}
                    {member.last_active_at && ` · last active ${new Date(member.last_active_at).toLocaleDateString()}`}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => openEdit(member)} title="Edit access"
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleResetPassword(member)} title="Reset password"
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                    <KeyRound className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleToggleActive(member)}
                    title={member.is_active ? 'Deactivate' : 'Reactivate'}
                    className={`p-2 rounded-lg ${member.is_active ? 'text-gray-500 hover:text-red-600 hover:bg-red-50' : 'text-gray-500 hover:text-green-600 hover:bg-green-50'}`}>
                    <Power className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">{editing ? `Edit access — ${editing.name}` : 'Add staff member'}</h2>
              <button onClick={() => setModalOpen(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {!editing && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full name</label>
                    <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. Musa Ibrahim" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Login details are sent here" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</label>
                    <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="+234…" />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role label (display only)</label>
                <input value={form.role_label} onChange={e => setForm(f => ({ ...f, role_label: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. Manager, Front Desk" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Permissions</label>

                {/* Quick-start presets */}
                {Object.keys(presets).length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    <span className="text-xs text-gray-400">Quick start:</span>
                    {Object.keys(presets).map(name => (
                      <button key={name} type="button" onClick={() => applyPreset(name)}
                        className="text-xs font-medium px-2.5 py-1 rounded-full border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100">
                        {PRESET_LABELS[name] ?? name}
                      </button>
                    ))}
                    <button type="button" onClick={() => setSelectedCaps([])}
                      className="text-xs font-medium px-2.5 py-1 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50">
                      Clear all
                    </button>
                  </div>
                )}

                {/* Grouped capabilities, laid out like the dashboard tabs */}
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-72 overflow-y-auto">
                  {renderGroups.map(group => {
                    const allSelected = group.capabilities.every(c => selectedCaps.includes(c));
                    return (
                      <div key={group.label}>
                        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 sticky top-0">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{group.label}</span>
                          <button type="button" onClick={() => toggleGroup(group.capabilities)}
                            className="text-xs font-medium text-blue-600 hover:text-blue-800">
                            {allSelected ? 'Clear' : 'Select all'}
                          </button>
                        </div>
                        {group.capabilities.map(cap => (
                          <label key={cap} className="flex items-center gap-3 px-3 py-2.5 text-sm cursor-pointer hover:bg-gray-50">
                            <input type="checkbox" checked={selectedCaps.includes(cap)} onChange={() => toggleCap(cap)}
                              className="rounded border-gray-300" />
                            <span className="text-gray-800">{CAPABILITY_LABELS[cap] ?? cap}</span>
                          </label>
                        ))}
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Money (earnings, payouts, transactions, bank details) and team management always stay owner-only.
                </p>
              </div>

              {modalError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">{modalError}</div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-5 border-t border-gray-100">
              <button onClick={() => setModalOpen(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={handleSave} disabled={saving || (!editing && (!form.name || !form.email))}
                className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg">
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Add staff member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
